import { btnDemo, tlink } from '../components/cta';
import { icon } from '../components/icons';
import { eyebrow, mesh } from '../components/primitives';
import { sigBand } from '../components/sections';
import { SITE_CONFIG } from '../config/site-config';
import { mailto, routeHref, salesMailto } from '../lib/links';

/* ---- Québec (French) ---- */
export function pageQuebec(): string {
  const caps = [
    ['inbox', 'Réception intelligente', 'Transformez les renseignements entrants en travail structuré.'],
    ['file', 'Intelligence documentaire', 'Transformez les documents non structurés en données de flux de travail.'],
    [
      'swap',
      'Intelligence des règlements',
      'Regroupez les offres, contre-offres et règlements au même endroit que le reste du dossier.',
    ],
    ['monitor', 'Portail client', 'Offrez à vos clients une visibilité contrôlée sur les dossiers qui les concernent.'],
    [
      'receipt',
      'Facturation et finances',
      'Finances des dossiers, facturation, suivi des honoraires et des paiements.',
    ],
    ['chart', 'Rapports et portefeuilles', 'Une vue d’ensemble, de l’organisation jusqu’à la parcelle et au dossier.'],
  ] as const;
  return `
  <div lang="fr-CA">
  <section class="hero compact" aria-labelledby="h1">
    ${mesh()}
    <div class="wrap rv">
      ${eyebrow('ReTax360 au Québec')}
      <h1 id="h1">L’IA + l’expertise. <span class="grad">Conçue autour de votre façon de travailler.</span></h1>
      <p class="lede" style="margin-top:24px;max-width:780px">ReTax360 est une plateforme d’orchestration de l’IA et de gestion des flux de travail conçue pour les opérations de fiscalité foncière. Elle réunit vos clients, vos communications, vos données et vos documents dans une seule fondation, puis coordonne le travail entre l’IA, les règles d’affaires, les systèmes et les professionnels responsables des décisions.</p>
      <div class="ctas">${btnDemo('Réserver une démo d’entreprise')}<a class="btn btn-ghost" href="${salesMailto()}">Nous écrire</a></div>
    </div>
  </section>

  <section class="s" aria-labelledby="fr-found-h">
    <div class="wrap">
      <div class="head rv">${eyebrow('Une seule fondation')}<h2 id="fr-found-h">Chaque cabinet travaille un peu différemment. La fondation, elle, ne change pas.</h2>
        <p>Clients. Communications. Données. Documents. Au-dessus de cette fondation se trouve une couche d’intelligence et d’orchestration, puis le flux de travail configuré selon votre organisation.</p></div>
      <ul class="grid3" data-stagger>
        <li class="card rv"><span class="ic-tile">${icon('workflow')}</span><h3>Votre flux de travail</h3><p>Réception, documents, contestations, preuves, échéances, règlements, communications, facturation et rapports, configurés selon votre organisation.</p></li>
        <li class="card rv"><span class="ic-tile">${icon('split')}</span><h3>Intelligence et orchestration</h3><p>Chaque tâche est acheminée vers la bonne ressource : règles, modèles d’IA, agents, API et systèmes, ou l’expert responsable.</p></li>
        <li class="card rv"><span class="ic-tile">${icon('layers')}</span><h3>Une seule fondation</h3><p>La base commune sous chaque flux de travail : clients, communications, données et documents.</p></li>
      </ul>
    </div>
  </section>

  ${sigBand('L’IA prépare. Les règles encadrent. Les experts décident.', { dark: true, grad: 'Les experts décident.' })}

  <section class="s" aria-labelledby="fr-cap-h">
    <div class="wrap">
      <div class="head rv">${eyebrow('Fonctionnalités')}<h2 id="fr-cap-h">Des applications configurées sur une fondation gouvernée.</h2>
        <p>Lorsque l’information est manquante ou contradictoire, que la confiance d’un résultat d’IA est faible ou qu’une décision exige un jugement professionnel, la tâche est transmise à la personne responsable, qui peut l’approuver, la modifier ou la rejeter.</p></div>
      <ul class="grid3" data-stagger>${caps.map(([i, h, p]) => `<li class="card rv"><span class="ic-tile">${icon(i)}</span><h3>${h}</h3><p>${p}</p></li>`).join('')}</ul>
    </div>
  </section>

  <section class="s alt" aria-labelledby="fr-na-h">
    <div class="wrap grid2" style="gap:48px;align-items:start">
      <div class="rv">${eyebrow('Canada · États-Unis')}<h2 id="fr-na-h" style="margin:16px 0">Conçue pour les opérations de fiscalité foncière en Amérique du Nord.</h2>
        <p class="lede">ReTax360 soutient les organisations de fiscalité foncière aux États-Unis et au Canada, avec des flux de travail configurables selon les juridictions, les marchés, les bureaux et les structures organisationnelles.</p></div>
      <div class="card rv"><span class="ic-tile">${icon('shield')}</span><h3>Sécurité et gouvernance</h3>
        <ul class="checks" style="margin-top:6px">
          <li>${icon('checkCircle')}<span>Authentification unique (SSO) prise en charge</span></li>
          <li>${icon('checkCircle')}<span>Contrôle d’accès basé sur les rôles et authentification multifacteur</span></li>
          <li>${icon('checkCircle')}<span>Points d’approbation humaine et traçabilité des actions</span></li>
          <li>${icon('checkCircle')}<span>Vos données opérationnelles demeurent votre propriété</span></li>
        </ul>
        <div style="margin-top:14px">${tlink('Sécurité et confiance (en anglais)', routeHref('/security'))}</div></div>
    </div>
  </section>

  <section class="s" id="loi-25" aria-labelledby="fr-priv-h">
    <div class="wrap narrow">
      <div class="head rv">${eyebrow('Loi 25')}<h2 id="fr-priv-h">Protection des renseignements personnels</h2></div>
      <div class="legal-sec rv">
        <p><strong>Responsable de la protection des renseignements personnels :</strong> Asumani Sula, cofondateur et chef de la direction, ReTax360 LLC — <a href="${mailto(SITE_CONFIG.INFO_EMAIL)}">${SITE_CONFIG.INFO_EMAIL}</a>.</p>
        <p>Nous recueillons les renseignements que vous nous fournissez (par exemple votre nom, votre courriel professionnel, votre organisation et les détails de votre demande) afin de répondre à votre demande, de fournir et d’améliorer nos services, d’en assurer la sécurité et de respecter nos obligations légales. <strong>Nous ne vendons pas vos renseignements personnels.</strong></p>
        <p>Vos renseignements peuvent être conservés et traités à l’extérieur du Québec et du Canada, notamment aux États-Unis, par nous ou par nos fournisseurs de services, avec des protections contractuelles appropriées.</p>
        <p>Vous pouvez demander l’accès à vos renseignements, leur rectification, leur communication dans un format technologique structuré, ou retirer votre consentement, en écrivant à <a href="${mailto(SITE_CONFIG.INFO_EMAIL)}">${SITE_CONFIG.INFO_EMAIL}</a>. Nous répondons dans un délai de 30 jours. Vous pouvez également porter plainte auprès de la Commission d’accès à l’information du Québec.</p>
        <p>Les témoins (cookies) d’analyse ne sont utilisés qu’avec votre consentement. Pour tous les détails, consultez notre <a href="${routeHref('/privacy#canada')}">politique de confidentialité</a> (en anglais).</p>
      </div>
      <p class="fine rv" style="margin-top:18px">Notre formulaire de demande de démo et nos pages détaillées sont actuellement offerts en anglais. Vous pouvez aussi nous écrire à <a class="ul" href="${salesMailto()}">${SITE_CONFIG.SALES_EMAIL}</a>.</p>
    </div>
  </section>

  <section class="cta dark" aria-labelledby="fr-cta-h">
    ${mesh('soft')}
    <div class="wrap rv">
      ${eyebrow('Démo d’entreprise')}
      <h2 id="fr-cta-h">L’IA + l’expertise. Conçue autour de votre façon de travailler.</h2>
      <p>Découvrez comment ReTax360 peut être configurée selon vos flux de travail, vos bureaux, vos marchés et vos exigences d’approbation.</p>
      <div class="ctas">${btnDemo('Réserver une démo d’entreprise')}</div>
    </div>
  </section>
  </div>`;
}
